import React, { useEffect, useState } from 'react';
import Table from '../../components/Table/Table';
import config from '../../config';
import AllSupplierPayments from '../../Models/SupplierForm/AllSupplierPayments';
import { Eye, Plus } from 'lucide-react';
import { useNavigate } from 'react-router';

function SupplierPayments() {
  const [rawData, setRawData] = useState([]);
  const [supplierSummary, setSupplierSummary] = useState([]);
  const [error, setError] = useState(null);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [selectedStockPay, setSelectedStockPay] = useState(null);
  const [dueFilter, setDueFilter] = useState('all');
  const [totalDue, setTotalDue] = useState(0);
  const [duePaymentsCount, setDuePaymentsCount] = useState(0);

  const navigate = useNavigate();

  const columns = [
    'Supplier Name',
    'Company Name',
    'Cheque Amount',
    'Cash Amount',
    'Total Quantity',
    'Vat %',
    'Total Amount',
    'Due Amount',
    'Actions',
  ];

  useEffect(() => {
    fetchStockPaymentsList();
    fetchDuePaymentsCount();
    fetchTotalDueAmount();
  }, []);

  useEffect(() => {
    if (rawData.length > 0) {
      processSupplierSummary();
    }
  }, [rawData, dueFilter]);

  const processSupplierSummary = () => {
    const supplierMap = new Map();

    rawData.forEach(item => {
      const supplierId = item.supplierId;
      if (!supplierMap.has(supplierId)) {
        supplierMap.set(supplierId, {
          supplier: item.supplier,
          chequeAmount: 0,
          cashAmount: 0,
          stockQty: 0,
          vat: 0,
          total: 0,
          due: 0,
          transactions: 0
        });
      }

      const summary = supplierMap.get(supplierId);
      summary.chequeAmount += parseFloat(item.chequeAmount || 0);
      summary.cashAmount += parseFloat(item.cashAmount || 0);
      summary.stockQty += parseFloat(item.stockQty || 0);
      summary.total += parseFloat(item.total || 0);
      summary.due += parseFloat(item.due || 0);
      summary.transactions += 1;
      summary.vat = ((summary.vat * (summary.transactions - 1) + parseFloat(item.vat || 0)) / summary.transactions);
    });

    let summaryData = Array.from(supplierMap.values()).map(summary => ({
      supplierName: summary.supplier?.supplierName || '-',
      supplierCompany: summary.supplier?.supplierCompany || '-',
      chequeAmount: summary.chequeAmount.toFixed(2),
      cashAmount: summary.cashAmount.toFixed(2),
      stockQty: summary.stockQty,
      vat: summary.vat,
      total: summary.total.toFixed(2),
      due: summary.due.toFixed(2),
      supplier: summary.supplier
    }));

    if (dueFilter === 'withDue') {
      summaryData = summaryData.filter(item => parseFloat(item.due) > 0);
    } else if (dueFilter === 'noDue') {
      summaryData = summaryData.filter(item => parseFloat(item.due) <= 0);
    }

    summaryData.sort((a, b) => parseFloat(b.due) - parseFloat(a.due));

    const formattedData = summaryData.map(item => [
      item.supplierName,
      item.supplierCompany,
      item.chequeAmount,
      item.cashAmount,
      item.stockQty,
      item.vat,
      item.total,
      item.due,
      <div className='d-flex' style={{ justifyContent: 'center' }}>
        <button className='btn btn-primary me-2' onClick={() => handleEditStockPay({ ...item, supplierId: item.supplier?.supplierId })}> <Plus /> Make A Payment</button>
        <button className='btn btn-info' onClick={() => handleViewClick(item.supplier?.supplierId, item.supplierName || 'unknown')} ><Eye /> </button>
      </div>
    ]);

    setSupplierSummary(formattedData);
  };

  const fetchStockPaymentsList = async () => {
    try {
      const response = await fetch(`${config.BASE_URL}/stockPayments`);
      if (!response.ok) {
        throw new Error(`Failed to fetch supplier payments list: ${response.status} ${response.statusText}`);
      }
      const stockPay = await response.json();
      setRawData(stockPay);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchDuePaymentsCount = async () => {
    try {
      const response = await fetch(`${config.BASE_URL}/stockPayments/dueCount`);
      if (!response.ok) {
        throw new Error('Failed to fetch due payments count');
      }
      const result = await response.json();
      setDuePaymentsCount(result.count || 0);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchTotalDueAmount = async () => {
    try {
      const response = await fetch(`${config.BASE_URL}/stockPayments/totalDues`);
      if (!response.ok) {
        throw new Error('Failed to fetch total due amount');
      }
      const result = await response.json();
      setTotalDue(result.totalDue || 0);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleViewClick = (supplierId, supplierName) => {
    navigate(`/supplier-AllDuePayments/${supplierId}`, { state: { supplierName } });
  };;

  const handleEditStockPay = (item) => {
    console.log("Opening Payment Modal for:", item);
    setSelectedStockPay(item);
    setShowPaymentHistoryModal(true);
  };

  const handleCloseModal = () => {
    setShowPaymentHistoryModal(false);
  };

  const title = 'Suppliers Due';
  const invoice = 'Suppliers Due.pdf';

  return (
    <div>
      <div className="scrolling-container">
        <div className="mt-4">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: "20px" }}>
            <div style={{ paddingLeft: "10px", width: "100%" }}>

              <h4 style={{ marginBottom: "24px", fontSize: "24px", color: "#2d3436", fontWeight: "600" }}>
                Supplier Due Invoices
              </h4>

              <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", flexWrap: "nowrap", overflowX: "auto", paddingBottom: "10px" }}>
                <div style={{ flex: "0 0 240px", backgroundColor: "#ffffff", padding: "16px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}>

                  <div style={{ fontSize: "14px", color: "#636e72", marginBottom: "8px" }}>
                    Filter
                  </div>

                  <select
                    style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid #dfe6e9", fontSize: "14px", backgroundColor: "#f8f9fa", outline: "none", cursor: "pointer" }}
                    value={dueFilter}
                    onChange={(e) => setDueFilter(e.target.value)}
                  >
                    <option value="all">All Payments</option>
                    <option value="withDue">With Due Amount</option>
                    <option value="noDue">No Due Amount</option>
                  </select>
                </div>

                <div style={{ flex: "1", backgroundColor: "#ffffff", padding: "16px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", minWidth: "220px" }}>
                  <div style={{ fontSize: "14px", color: "#636e72", marginBottom: "12px" }}>
                    Total Due Payments
                  </div>

                  <input
                    type="text"
                    style={{ width: "100%", padding: "12px", fontSize: "25px", fontWeight: "600", color: "#e74c3c", border: "none", backgroundColor: "transparent", textAlign: "right" }}
                    value={duePaymentsCount}
                    disabled
                  />
                </div>

                <div style={{ flex: "1", backgroundColor: "#ffffff", padding: "16px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", minWidth: "220px" }}>
                  <div style={{ fontSize: "14px", color: "#636e72", marginBottom: "12px" }}>
                    Total Due Amount
                  </div>

                  <input
                    type="text"
                    style={{ width: "100%", padding: "12px", fontSize: "25px", fontWeight: "600", color: "#e74c3c", border: "none", backgroundColor: "transparent", textAlign: "right" }}
                    value={`LKR ${totalDue.toLocaleString()}`}
                    disabled
                  />
                </div>

              </div>
            </div>
          </div>
        </div>

        <Table
          search="Search by Supplier Name"
          data={supplierSummary}
          columns={columns}
          showButton={false}
          showDate={false}
          title={title}
          invoice={invoice}
          showActions={false}
          showDelete={false}
        />

        {showPaymentHistoryModal && (
          <AllSupplierPayments
            showModal={showPaymentHistoryModal}
            closeModal={handleCloseModal}
            supplierName={selectedStockPay?.supplierName}
            supplierId={selectedStockPay?.supplierId}
          />
        )}
      </div>
      {error && <p style={{ color: 'red' }}>{error}</p>}
    </div>
  );
}

export default SupplierPayments;